import type { InjectionKey, MaybeRefOrGetter } from "vue";
import {
  hasRenditions,
  productAvifSrcset,
  productWebpSrc,
  renditionUrl,
} from "~/utils/cdn";
import { hasProductSeoUrl } from "./canonicalPath";
import { readStoredCatalogMobileColumns } from "./catalogMobileColumns";
import { productMediaPicture } from "./productMedia";

/**
 * `sizes` of a product card image outside the catalog grid: product
 * carousels (fixed 236/265/325px columns on desktop), the wishlist grid and
 * the other rails. The real card image box is ~178px (mobile) → ~300px
 * (desktop). Declaring the true desktop box (300px, not 25vw≈360) keeps DPR2
 * desktop on w640 (300×2=600) instead of jumping to w828. Only DPR3 desktop
 * (rare) is marginally soft. The catalog grid has its own, wider
 * CATALOG_GRID_IMAGE_SIZES.
 */
export const PRODUCT_CARD_IMAGE_SIZES =
  "(max-width: 600px) 50vw, (max-width: 1024px) 33vw, 300px";

/**
 * `sizes` of a card image in the catalog result grid (category, search and
 * manufacturer pages; organisms/catalog-product) with two cards per row on
 * phones. Card boxes, from the grid CSS and measured on live 2026-10-16:
 * - up to 767px: two cards, 12px side padding, 10px gap, so the box is
 *   50vw − 17px (measured 178px at 390, 355px at 744);
 * - 768–1199px: three cards, 20px padding, 10px gaps: 33.33vw − 20px, less a
 *   third of a classic scrollbar (measured with a 10px one: 232.66px at 768,
 *   318px at 1024, 374.66px at 1194);
 * - from 1200px: four cards with 20px gaps inside --vf-shop-content-max
 *   (scss/theme.scss), 1360px below 1600px and min(1680px, 90vw) from
 *   1600px. The box is at most 325px up to 1599px (283px at 1280),
 *   22.5vw − 15px from 1600px and 405px from 1867px (345/363/405px at
 *   1600/1680/1867+).
 * Below 1200px a card declares the viewport divided by the cards per row:
 * 17px (two cards) or 20px (three) more than its box, plus a share of a
 * classic scrollbar on desktops. The owner wants phone and tablet photos sharp
 * first (2026-10-16), and that margin keeps every iPhone and iPad on the
 * rung it had or a larger one (iPad landscape and iPad mini 6 portrait move
 * from w640 to w828). From 1600px the true box is declared: DPR1 takes w480
 * once the box passes 360px, DPR2 takes w828. From 1200px to 1599px it stays
 * 300px: declaring 325px would move every DPR2 desktop (MacBook 1440/1512)
 * from w640 to w828 for a 1.5% larger box.
 */
export const CATALOG_GRID_IMAGE_SIZES =
  "(max-width: 767px) 50vw, (max-width: 1199px) 33.33vw, (min-width: 1867px) 405px, (min-width: 1600px) calc(22.5vw - 15px), 300px";

/**
 * CATALOG_GRID_IMAGE_SIZES in the one-card-per-row phone mode
 * (utils/catalogMobileColumns): up to 480px the card is the full width less
 * the 12px padding on each side (measured 366px at 390), declared 100vw
 * (24px more than the box), so a 3x iPhone takes
 * w1000, the whole 1000px master. A `sizes` media query cannot see the
 * mode, so the grid provides this string while the mode is on.
 */
export const CATALOG_GRID_ONE_COLUMN_IMAGE_SIZES = `(max-width: 480px) 100vw, ${CATALOG_GRID_IMAGE_SIZES}`;

/** The catalog grid's card `sizes` for its phone mode (1 or 2 cards per row). */
export const catalogGridImageSizes = (mobileColumns: unknown): string =>
  mobileColumns === 1
    ? CATALOG_GRID_ONE_COLUMN_IMAGE_SIZES
    : CATALOG_GRID_IMAGE_SIZES;

/**
 * The `sizes` a listing gives the card images under it (provide/inject): a
 * string, or a ref when it changes with the listing (the catalog grid's phone
 * mode). A card with no provider uses PRODUCT_CARD_IMAGE_SIZES.
 */
export const PRODUCT_CARD_IMAGE_SIZES_KEY: InjectionKey<
  MaybeRefOrGetter<string>
> = Symbol("productCardImageSizes");

/** What a product card's <picture> (molecules/new-product-thumb-image) requests. */
export type ProductCardImageRequest = {
  /** <img src>: the 600 square AVIF when renditions exist, else the API image. */
  src: string;
  /** <source type="image/avif" srcset>, "" for a raster-only product. */
  avifSrcset: string;
  /** <source type="image/webp" srcset>, "" for a raster-only product. */
  webpSrc: string;
  /** <img srcset>: the JPEG of a photo's `media`; absent on the legacy path. */
  imgSrcset?: string;
  sizes: string;
};

/**
 * The request of a card image for an API image URL, or null when the card
 * shows the placeholder. Cards render small, so the 600 square AVIF is the
 * <img> source at every breakpoint when the product has AVIF renditions;
 * products with no AVIF master keep their raster image and get no sources.
 */
export const productCardImageRequest = (
  image: unknown,
  sizes: string = PRODUCT_CARD_IMAGE_SIZES
): ProductCardImageRequest | null => {
  if (!image) return null;
  const url = image as string;
  return {
    src: hasRenditions(url)
      ? renditionUrl(url, 600, { ratio: 1, format: "avif" }) || url
      : url,
    avifSrcset: productAvifSrcset(url),
    webpSrc: productWebpSrc(url),
    sizes,
  };
};

/**
 * The request of a card image for the photo's `media` (utils/productMedia),
 * or null without usable `media`: every AVIF rung for the card's `sizes`, so
 * the browser takes the rung of the card box and DPR, and the JPEG as the
 * <img> for browsers without AVIF. No WebP source (phase-6 plan §2).
 */
export const productCardMediaRequest = (
  media: unknown,
  sizes: string = PRODUCT_CARD_IMAGE_SIZES
): ProductCardImageRequest | null => {
  const picture = productMediaPicture(media);
  if (!picture) return null;
  return {
    src: picture.jpegSrc,
    avifSrcset: picture.avifSrcset,
    webpSrc: "",
    imgSrcset: picture.jpegSrcset,
    sizes,
  };
};

/**
 * What a product card asks for first: its photo's own `media`, else the
 * legacy request of its `image`. The card falls back to the legacy request
 * when the `media` photo fails (molecules/new-product-thumb-image).
 */
export const productCardRequest = (
  product: any,
  sizes: string = PRODUCT_CARD_IMAGE_SIZES
): ProductCardImageRequest | null =>
  productCardMediaRequest(product?.media, sizes) ||
  productCardImageRequest(product?.image, sizes);

/**
 * Card images warmed for a prefetched result page: the first four cards, the
 * grid's priority (eager) cards.
 */
export const CATALOG_WARM_IMAGE_LIMIT = 4;

// The catalog-grid `sizes` each card photo was first requested with in this
// tab, least recently used first. A photo keeps them when the phone mode
// changes, so a card that remounts (the grid unmounts far rows) asks for the
// rung it already has instead of downloading the photo again (owner,
// 2026-10-16).
const catalogPhotoSizes = new Map<string, string>();
const MAX_REMEMBERED_CATALOG_PHOTOS = 400;

const isCatalogGridSizes = (sizes: string) =>
  sizes === CATALOG_GRID_IMAGE_SIZES ||
  sizes === CATALOG_GRID_ONE_COLUMN_IMAGE_SIZES;

/** Identity of a card's photo: its `media` JPEG and its legacy image. */
export const productCardPhotoKey = (product: any): string =>
  `${productCardMediaRequest(product?.media)?.src || ""}|${
    productCardImageRequest(product?.image)?.src || ""
  }`;

/**
 * Records that a catalog card photo was requested with these `sizes`; the
 * first request's sizes stay, a later one only marks the photo as recently
 * used. Browser only; other listings' sizes are not recorded.
 */
export const rememberCatalogCardPhoto = (key: string, sizes: string) => {
  if (
    typeof window === "undefined" ||
    key === "|" ||
    !isCatalogGridSizes(sizes)
  )
    return;
  const first = catalogPhotoSizes.get(key) ?? sizes;
  catalogPhotoSizes.delete(key);
  catalogPhotoSizes.set(key, first);
  while (catalogPhotoSizes.size > MAX_REMEMBERED_CATALOG_PHOTOS) {
    const oldest = catalogPhotoSizes.keys().next().value;
    if (oldest === undefined) break;
    catalogPhotoSizes.delete(oldest);
  }
};

/**
 * The `sizes` a card asks for its photo with: in the catalog grid, the sizes
 * the photo was already requested with in this tab, else the listing's own
 * (always the listing's on the server, so hydration matches).
 */
export const cardPhotoSizes = (key: string, listingSizes: string): string =>
  (typeof window !== "undefined" &&
    isCatalogGridSizes(listingSizes) &&
    catalogPhotoSizes.get(key)) ||
  listingSizes;

// Warm-ups still loading; holding them keeps a detached image from being
// collected (and its request cancelled) before it lands in the HTTP cache.
const pendingWarmups = new Set<HTMLImageElement>();
const MAX_PENDING_WARMUPS = 2 * CATALOG_WARM_IMAGE_LIMIT;
// Requests already started in this tab, oldest first.
const warmedRequests = new Set<string>();
const MAX_REMEMBERED_REQUESTS = 96;

const rememberWarmed = (signature: string) => {
  warmedRequests.add(signature);
  while (warmedRequests.size > MAX_REMEMBERED_REQUESTS) {
    const oldest = warmedRequests.values().next().value;
    if (oldest === undefined) break;
    warmedRequests.delete(oldest);
  }
};

type WarmupDocument = Pick<Document, "createElement">;

// The sizes count too: a catalog card and a carousel card of one photo take
// different rungs on wide desktops.
const requestSignature = (request: ProductCardImageRequest) =>
  [
    request.avifSrcset,
    request.webpSrc,
    request.imgSrcset || "",
    request.src,
    request.sizes,
  ].join("|");

/**
 * Loads one card image the way the card's own <picture> will: a detached
 * <picture> with the same typed sources, <img> srcset and sizes, so the
 * browser picks the same candidate (the AVIF rung or the JPEG of `media`, or
 * the legacy AVIF or WebP, for the viewport and DPR) and the card later finds
 * it in the HTTP cache. No `loading` attribute: a detached
 * lazy image never loads.
 */
const startPictureLoad = (
  doc: WarmupDocument,
  request: ProductCardImageRequest,
  priority?: "high" | "low"
) => {
  const picture = doc.createElement("picture");
  const addSource = (type: string, srcset: string) => {
    if (!srcset) return;
    const source = doc.createElement("source");
    source.type = type;
    source.srcset = srcset;
    source.sizes = request.sizes;
    picture.appendChild(source);
  };
  addSource("image/avif", request.avifSrcset);
  addSource("image/webp", request.webpSrc);

  const img = doc.createElement("img");
  img.decoding = "async";
  if (priority) img.fetchPriority = priority;
  const settle = () => {
    img.onload = null;
    img.onerror = null;
    pendingWarmups.delete(img);
  };
  img.onload = settle;
  img.onerror = settle;
  pendingWarmups.add(img);
  // The <img> joins the <picture> before it gets a source, so the source
  // selection already sees the typed <source> siblings.
  picture.appendChild(img);
  if (request.imgSrcset) {
    img.srcset = request.imgSrcset;
    img.sizes = request.sizes;
  }
  img.src = request.src;
};

/**
 * Starts loading the first card images of a result page (at most
 * CATALOG_WARM_IMAGE_LIMIT), in grid order, exactly as those cards will
 * request them: with the catalog grid's `sizes` for the phone mode
 * (catalogGridImageSizes; the stored mode unless `mobileColumns` is given),
 * or the sizes a photo was already requested with (cardPhotoSizes). The
 * caller owns the network gate. Returns how many loads started; images
 * already warmed in this tab are not requested again.
 */
export const warmProductCardImages = (
  products: unknown,
  options: {
    limit?: number;
    doc?: WarmupDocument | null;
    mobileColumns?: number | null;
  } = {}
): number => {
  const doc =
    options.doc ?? (typeof document !== "undefined" ? document : null);
  if (!doc || !Array.isArray(products)) return 0;
  const limit = Math.min(
    CATALOG_WARM_IMAGE_LIMIT,
    Math.max(0, Math.floor(Number(options.limit ?? CATALOG_WARM_IMAGE_LIMIT)))
  );
  const sizes = catalogGridImageSizes(
    options.mobileColumns === undefined
      ? readStoredCatalogMobileColumns()
      : options.mobileColumns
  );

  let started = 0;
  // The grid gives no card to a product without a SEO URL
  // (organisms/new-product-grid), so those are not "first cards" either.
  const firstCards = products
    .filter((product: any) => product && hasProductSeoUrl(product))
    .slice(0, limit);
  for (const product of firstCards) {
    if (pendingWarmups.size >= MAX_PENDING_WARMUPS) break;
    const key = productCardPhotoKey(product);
    const request = productCardRequest(product, cardPhotoSizes(key, sizes));
    if (!request) continue;
    const signature = requestSignature(request);
    if (warmedRequests.has(signature)) continue;
    rememberWarmed(signature);
    startPictureLoad(doc, request);
    rememberCatalogCardPhoto(key, request.sizes);
    started += 1;
  }
  return started;
};

/**
 * Warms the card request of one product whose photo has usable `media`, for
 * a product carousel that preloads its slides (molecules/splide-carousel), so
 * the preload is the photo the card paints rather than the legacy image.
 * True when the product's card shows its `media` (warmed now or earlier in
 * this tab); false otherwise, and the caller keeps its own preload.
 */
export const warmProductCardMedia = (
  product: unknown,
  options: { priority?: "high" | "low"; doc?: WarmupDocument | null } = {}
): boolean => {
  const request = productCardMediaRequest((product as any)?.media);
  if (!request) return false;
  const doc =
    options.doc ?? (typeof document !== "undefined" ? document : null);
  if (!doc) return true;
  const signature = requestSignature(request);
  if (!warmedRequests.has(signature)) {
    rememberWarmed(signature);
    startPictureLoad(doc, request, options.priority);
  }
  return true;
};

/** Test hook: forget warmed requests, pending loads and requested photos. */
export const resetProductCardImageWarmup = () => {
  pendingWarmups.clear();
  warmedRequests.clear();
  catalogPhotoSizes.clear();
};
